import { useQuery } from '@tanstack/react-query';
import { SchoolApi } from '../api/client';
import { useAuth } from '../../utils/Auth/AuthProvider';

/** A teacher's own assignments (me/teaching/): what the "viewing as" switch offers, and
 *  the only grades and subjects they may create work for (homeroom gives no authoring). */
export function useMyTeaching() {
  const { role } = useAuth();
  return useQuery({
    queryKey: ['me.teaching'],
    queryFn: () => SchoolApi.meTeaching(),
    enabled: role === 'teacher',
    staleTime: 5 * 60 * 1000,
  });
}

/** Subjects the viewer may create work for, per grade: null = no limit (not a teacher). */
export function useTaughtSubjects(): {
  ready: boolean;
  grades: number[] | null;
  /** The grades themselves (id + name), whatever the "viewing as" lens shows elsewhere. */
  gradeRows: { id: number; name: string }[] | null;
  forGrade: (gradeId: number | null | undefined) => number[] | null;
  /** Sections the viewer may give work of this grade and subject to; null = the whole grade. */
  sectionsFor: (gradeId: number | null | undefined, subjectId: number | null | undefined) => number[] | null;
} {
  const { role } = useAuth();
  const q = useMyTeaching();
  // Not a teacher, or the assignments are not in yet: no limit here (the server checks anyway).
  if (role !== 'teacher' || !q.isSuccess) {
    return { ready: role !== 'teacher' || q.isError, grades: null, gradeRows: null, forGrade: () => null, sectionsFor: () => null };
  }
  const rows = q.data?.subjects ?? [];
  const gradeRows = Array.from(new Map(rows.map((r) => [r.grade.id, r.grade])).values());
  return {
    ready: true,
    grades: gradeRows.map((g) => g.id),
    gradeRows,
    forGrade: (gradeId) => Array.from(new Set(rows.filter((r) => r.grade.id === gradeId).map((r) => r.subject.id))),
    sectionsFor: (gradeId, subjectId) => {
      const mine = rows.filter((r) => r.grade.id === gradeId && r.subject.id === subjectId);
      if (mine.length === 0 || mine.some((r) => r.section == null)) return null;  // grade-wide (or not decided yet)
      return Array.from(new Set(mine.map((r) => r.section!.id)));
    },
  };
}
